/**
 * Organizational charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const orgChartsPack: ToilePack = {
    slug: 'org-charts',
    category: 'office',
    name: 'Organizational charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'hierarchy', label: 'Hierarchy', subject: 'an org chart with boxes for CEO, managers and teams connected by lines'},
        {key: 'flat', label: 'Flat', subject: 'a flat org chart with teams side by side'},
        {key: 'matrix', label: 'Matrix', subject: 'a matrix org chart'},
    ],
    styles: [
        {key: 'boxes', label: 'Boxes', style: 'plain rectangular boxes joined by thin right-angle connector lines, bold top box', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'colour', label: 'Colour coded', style: 'boxes filled in one colour per level, joined by clean connector lines', palette: 'navy, teal, orange and light grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'rounded', label: 'Rounded', style: 'soft rounded cards with small empty avatar circles, joined by smooth curved connectors', palette: 'purple and light grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['circle nodes', 'dark mode', 'with photos'],
    directionHint: 'try: circle nodes',
    seo: {
        title: 'Org chart: hierarchy, flat and matrix',
        metaDescription: 'Nine printable org charts: a hierarchy, a flat and a matrix chart, each in boxed, colour coded or rounded style.',
        h1: 'Org chart: hierarchy, flat, matrix',
        tagline: 'Three company chart layouts, each in three styles, with empty boxes ready for names and roles.',
        intro: 'An org chart shows who reports to whom in an organisation. These nine show a hierarchy chart with a CEO, managers and teams; a flat chart with teams side by side; and a matrix chart. Only the CEO and Manager headings are lettered; the other boxes hold grey lines. Type a twist, such as circle nodes, and it is a request.',
        useCases: [
            "a small company's team chart",
            "a staff chart for a new hire's first week",
            'a committee chart for a club or school',
            'a wall chart for an office',
        ],
        faq: [
            {q: 'What is the difference between hierarchy, flat and matrix?', a: 'A hierarchy chart stacks levels from the top box down. A flat chart puts teams side by side with few levels. A matrix chart shows people reporting along two lines.'},
            {q: 'Are the names filled in?', a: 'No. The boxes hold grey lines. You type the names and roles yourself.'},
            {q: 'Can I use it for a club or a school?', a: 'Yes. The boxes carry no fixed names, so the same layout works for any group.'},
            {q: 'Can I have photos in the boxes?', a: 'Type it as a twist, such as with photos. It is a request for photos, not a promised result.'},
        ],
    },
}
